import * as React from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select from "@amzn/awsui-components-react/polaris/select";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import { API } from "aws-amplify";
import { fetchAllConfigs } from "./cpcp-config-api";

interface ConfigItem {
  featureName: string;
  customerTypeName: string;
}

interface SelectOption {
  label?: string;
  value?: string;
}

interface AuditChange {
  field: string;
  oldValue?: string;
  newValue?: string;
}

interface AuditLogEntry {
  configKey: string;
  timestamp: number;
  updatedBy?: string;
  action: string;
  changes?: AuditChange[];
}

interface State {
  configs: ConfigItem[];
  allEntries: AuditLogEntry[];
  loading: boolean;
  error: string | null;
  filterFeature: SelectOption | null;
  filterType: SelectOption | null;
  currentPage: number;
}

const PAGE_SIZE = 50;

// Max concurrent audit-log requests when fanning out over the (now unbounded)
// config list, so a large config table can't trigger thousands of simultaneous
// API calls.
const AUDIT_FETCH_CONCURRENCY = 10;

class AuditLog extends React.Component<any, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      configs: [],
      allEntries: [],
      loading: true,
      error: null,
      filterFeature: null,
      filterType: null,
      currentPage: 1,
    };
  }

  componentDidMount() {
    this.loadAll();
  }

  loadAll = async () => {
    this.setState({ loading: true, error: null });
    try {
      const configs: ConfigItem[] = await fetchAllConfigs<ConfigItem>();
      this.setState({ configs });

      // Fetch audit entries per config. The config list is now unbounded
      // (fetchAllConfigs follows nextToken across all pages), so cap the
      // in-flight requests instead of issuing one Promise per config at
      // once, which could throttle the API or exhaust browser connections.
      const allEntries: AuditLogEntry[] = [];
      for (let i = 0; i < configs.length; i += AUDIT_FETCH_CONCURRENCY) {
        const batch = configs.slice(i, i + AUDIT_FETCH_CONCURRENCY);
        const results = await Promise.all(
          batch.map(cfg =>
            API.get("CpcpV2API", `/cpcp/v2/config/${cfg.featureName}/${cfg.customerTypeName}/audit`, {
              queryStringParameters: { limit: 50 },
            }).catch(() => ({ entries: [] }))
          )
        );
        for (const result of results) {
          if (result.entries) {
            allEntries.push(...result.entries);
          }
        }
      }
      allEntries.sort((a, b) => b.timestamp - a.timestamp);
      this.setState({ allEntries, loading: false });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to load audit data", loading: false });
    }
  };

  getFilteredEntries = (): AuditLogEntry[] => {
    const { allEntries, filterFeature, filterType } = this.state;
    return allEntries.filter(entry => {
      if (filterFeature?.value && !entry.configKey.startsWith(filterFeature.value + "/")) return false;
      if (filterType?.value && !entry.configKey.endsWith("/" + filterType.value)) return false;
      return true;
    });
  };

  render() {
    const { configs, loading, error, filterFeature, filterType, currentPage } = this.state;

    if (loading) {
      return (
        <SpaceBetween size="l">
          <Header variant="h2">Audit Log</Header>
          <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>
        </SpaceBetween>
      );
    }

    const featureOptions = [...new Set(configs.map(c => c.featureName))].sort()
      .map(f => ({ label: f, value: f }));
    const typeOptions = [...new Set(
      configs
        .filter(c => !filterFeature?.value || c.featureName === filterFeature.value)
        .map(c => c.customerTypeName)
    )].sort().map(t => ({ label: t, value: t }));

    const filtered = this.getFilteredEntries();
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageEntries = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
      <SpaceBetween size="l">
        <Header variant="h2">Audit Log</Header>

        {error && (
          <Flashbar items={[{
            type: "error",
            content: error,
            dismissible: true,
            onDismiss: () => this.setState({ error: null }),
          }]} />
        )}

        <SpaceBetween size="s" direction="horizontal">
          <FormField label="Feature Name">
            <Select
              selectedOption={filterFeature}
              onChange={({ detail }) => this.setState({ filterFeature: detail.selectedOption, filterType: null, currentPage: 1 })}
              options={[{ label: "All", value: "" }, ...featureOptions]}
              placeholder="All"
            />
          </FormField>
          <FormField label="Customer Type Name">
            <Select
              selectedOption={filterType}
              onChange={({ detail }) => this.setState({ filterType: detail.selectedOption, currentPage: 1 })}
              options={[{ label: "All", value: "" }, ...typeOptions]}
              placeholder="All"
            />
          </FormField>
          <FormField label=" ">
            <Button iconName="refresh" onClick={this.loadAll} />
          </FormField>
        </SpaceBetween>

        <Table
          header={
            <Header counter={`(${filtered.length})`}>
              Recent Changes
            </Header>
          }
          columnDefinitions={[
            { id: "timestamp", header: "When", cell: (e: AuditLogEntry) => new Date(e.timestamp).toLocaleString(), width: 200 },
            { id: "configKey", header: "Config", cell: (e: AuditLogEntry) => e.configKey, width: 200 },
            { id: "updatedBy", header: "Who", cell: (e: AuditLogEntry) => e.updatedBy || "—", width: 140 },
            { id: "action", header: "Action", cell: (e: AuditLogEntry) => e.action, width: 100 },
            { id: "changes", header: "Changes", cell: (e: AuditLogEntry) => {
              if (!e.changes || e.changes.length === 0) return "—";
              const truncate = (v: string | undefined, max: number) => {
                if (!v) return "—";
                return v.length > max ? v.substring(0, max) + "…" : v;
              };
              return e.changes.map((c, i) => (
                <div key={i} title={`${c.field}: ${truncate(c.oldValue, 200)} → ${truncate(c.newValue, 200)}`}>
                  <b>{c.field}</b>: {truncate(c.oldValue, 80)} &rarr; {truncate(c.newValue, 80)}
                </div>
              ));
            }},
          ]}
          items={pageEntries}
          empty={<Box textAlign="center" color="inherit" padding="l">No audit entries found</Box>}
          pagination={
            totalPages > 1 ? (
              <Pagination
                currentPageIndex={currentPage}
                pagesCount={totalPages}
                onChange={({ detail }) => this.setState({ currentPage: detail.currentPageIndex })}
              />
            ) : undefined
          }
        />
      </SpaceBetween>
    );
  }
}

export default AuditLog;
